/**
 * LiveSceneThumb — renders a thumbnail of a single scene as a stilled
 * Hyperframes preview, paused at ~40% into the scene. Lazy-mounts when
 * scrolled into view so dense LayersPanels stay snappy.
 */
import React, { useEffect, useRef, useState } from "react";
import { HyperframesPlayer, type HfPlayerRef } from "../../hyperframesPlayer";
import { FORMAT_DIMENSIONS, FPS, buildReelComposition } from "../../lib/hyperframesCompositions";
import type { ReelScene } from "../../api";
import type { QueueItem } from "@engine/lib/queue";
import type { FormatId } from "@engine/hyperframes/format";

type Props = {
  scene: ReelScene;
  format: string;
  width?: number;
  frameToDisplay?: number;
  /** Optionales Theme-Override für die Vorschau (sonst folgt es scene/extras). */
  theme?: "dark" | "light";
};

class ThumbErrorBoundary extends React.Component<
  { children: React.ReactNode; onError?: () => void },
  { error: boolean }
> {
  constructor(props: { children: React.ReactNode; onError?: () => void }) {
    super(props);
    this.state = { error: false };
  }
  static getDerivedStateFromError(): { error: boolean } {
    return { error: true };
  }
  componentDidCatch(): void {
    this.props.onError?.();
  }
  render() {
    if (this.state.error) return null;
    return this.props.children as React.ReactElement;
  }
}

export const LiveSceneThumb: React.FC<Props> = ({
  scene,
  format,
  width = 64,
  frameToDisplay,
  theme,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<HfPlayerRef | null>(null);
  const [mounted, setMounted] = useState(false);
  const [crashed, setCrashed] = useState(false);

  useEffect(() => {
    if (mounted) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setMounted(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "120px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mounted]);

  const dims = FORMAT_DIMENSIONS[(format as FormatId)] ?? FORMAT_DIMENSIONS["9:16"];
  const aspect = dims.height / dims.width;
  const height = Math.round(width * aspect);

  const durFrames = Math.max(1, Math.round((scene.durationSec ?? 3) * FPS));
  const frame = Math.min(durFrames - 1, Math.max(0, frameToDisplay ?? Math.round(durFrames * 0.4)));

  const composition = React.useMemo(() => {
    const item = {
      id: `thumb_${scene.uid}`,
      kind: "reel",
      template: "HookReveal",
      category: "Reel",
      title: "Thumb",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      props: { de: {}, en: {}, es: {}, fr: {} },
      languages: ["de"],
      formats: [format],
      status: "pending",
      outputs: [],
      ...(theme ? { theme } : {}),
      scenes: [
        { ...scene, transition: undefined, fromSec: undefined, track: 0 },
      ],
    } as unknown as QueueItem;
    return buildReelComposition(item, format as FormatId, "de");
  }, [scene, format, theme]);

  return (
    <div
      ref={ref}
      className="live-scene-thumb"
      style={{ width, height }}
      title={scene.sceneId}
    >
      {mounted && !crashed ? (
        <ThumbErrorBoundary onError={() => setCrashed(true)}>
          <HyperframesPlayer
            key={theme ?? "dark"}
            srcDoc={composition.html}
            compositionWidth={Math.max(160, width * 4)}
            compositionHeight={Math.max(160, height * 4)}
            fps={FPS}
            displayWidth={width}
            displayHeight={height}
            controls={false}
            onReady={(p) => {
              playerRef.current = p;
              p?.seekTo(frame);
              p?.pause();
            }}
            style={{ borderRadius: 6, background: theme === "light" ? "#f4f6fa" : "#06070b" }}
          />
        </ThumbErrorBoundary>
      ) : null}
    </div>
  );
};
